/**
 * Einen Zeitraum zurücksetzen.
 *
 * Der schwerste Eingriff in der Anwendung, deshalb in drei Schritten: Umfang
 * wählen, sehen was verschwindet, mit Grund bestätigen. Der mittlere Schritt
 * ist der wichtige — eine Löschung, deren Umfang man erst hinterher sieht, ist
 * keine Entscheidung, sondern ein Unfall.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PeriodDto } from "../lib/api";
import { euro, longDate } from "../lib/format";
import { ErrorBox, sourceLabel } from "./ui";

interface Vorschau {
  from: string;
  to: string;
  betriebstage: number;
  davonFreigegeben: number;
  davonGesperrt: number;
  kassensessions: number;
  auszahlungszeilen: number;
  betrag: string;
  auszahlungsperioden: { id: string; label: string; status: string }[];
  importe: { id: string; filename: string | null; sourceType: string; zeilen: number }[];
  importeTeilweise: {
    id: string;
    filename: string | null;
    sourceType: string;
    from: string | null;
    to: string | null;
  }[];
  altbestandszeilen: number;
}

type Umfang = "zeitraum" | "monat";

export function ResetPeriod({
  periode,
  onDone,
  onCancel,
}: {
  periode: PeriodDto;
  onDone: (sicherung: string | null) => void;
  onCancel: () => void;
}) {
  const [umfang, setUmfang] = useState<Umfang>("zeitraum");
  const [mitImporten, setMitImporten] = useState(true);
  const [grund, setGrund] = useState("");
  const queryClient = useQueryClient();

  // Ganzer Monat: vom Ersten bis zum Letzten. Der Zeitraum kennt seinen Monat,
  // die Monatslänge steht im Enddatum der zweiten Hälfte.
  const monatsEnde = new Date(
    Date.UTC(Number(periode.month.slice(0, 4)), Number(periode.month.slice(5, 7)), 0),
  );
  const bereich =
    umfang === "monat"
      ? {
          from: `${periode.month}-01`,
          to: `${periode.month}-${String(monatsEnde.getUTCDate()).padStart(2, "0")}`,
        }
      : { from: periode.from, to: periode.to };

  const vorschau = useQuery({
    queryKey: ["reset-preview", bereich.from, bereich.to, mitImporten],
    queryFn: () =>
      api.get<Vorschau>(
        `/business-days/reset-preview?from=${bereich.from}&to=${bereich.to}&mitImporten=${mitImporten}`,
      ),
  });

  const loeschen = useMutation({
    mutationFn: () =>
      api.post<{ sicherung: string | null }>("/business-days/reset", {
        ...bereich,
        mitImporten,
        reason: grund.trim(),
      }),
    onSuccess: async (d) => {
      setGrund("");
      await queryClient.invalidateQueries();
      onDone(d.sicherung);
    },
  });

  const v = vorschau.data;
  const betrifftAusgezahltes = (v?.davonGesperrt ?? 0) > 0 || (v?.auszahlungsperioden.length ?? 0) > 0;
  const nichts = v !== undefined && v.betriebstage === 0 && v.importe.length === 0;

  return (
    <div className="card border-alert/40 p-5">
      <h3 className="mb-1 text-lg">Zeitraum zurücksetzen</h3>
      <p className="mb-4 max-w-2xl text-sm text-muted">
        Löscht Betriebstage, Kassensessions und Auszahlungen. Danach lässt sich der Zeitraum
        neu importieren und rechnen. In der Software ist das nicht rückgängig zu machen.
        Deshalb wird vorher automatisch die ganze Datenbank gesichert.
      </p>

      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            ["zeitraum", periode.label],
            ["monat", `ganzer ${periode.monthLabel}`],
          ] as const
        ).map(([wert, text]) => (
          <button
            key={wert}
            className={umfang === wert ? "btn-primary" : "btn-outline"}
            onClick={() => setUmfang(wert)}
          >
            {text}
          </button>
        ))}
      </div>

      <label className="mb-4 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={mitImporten}
          onChange={(e) => setMitImporten(e.target.checked)}
        />
        <span>
          Importe mitlöschen
          <span className="block text-xs text-muted">
            Nur Stapel, die ganz in diesem Zeitraum liegen. Was über den Rand reicht, bleibt.
            Sonst verschwänden Zeilen eines Monats, den niemand löschen wollte.
          </span>
        </span>
      </label>

      {vorschau.isLoading ? <p className="text-sm text-muted">Zählt …</p> : null}
      {vorschau.error ? <ErrorBox error={vorschau.error} /> : null}

      {v ? (
        <div className="mb-4 rounded border border-line bg-surface px-4 py-3 text-sm">
          <div className="label mb-2">Das verschwindet</div>
          {nichts ? (
            <p className="text-muted">Nichts. In diesem Zeitraum liegen keine Daten.</p>
          ) : (
            <ul className="space-y-1">
              <li>
                <strong>{v.betriebstage}</strong> Betriebstage
                {v.davonFreigegeben > 0 ? `, davon ${v.davonFreigegeben} freigegeben` : ""}
                {v.davonGesperrt > 0 ? `, ${v.davonGesperrt} gesperrt` : ""}
              </li>
              <li>
                <strong>{v.kassensessions}</strong> Kassensessions
              </li>
              <li>
                <strong>{v.auszahlungszeilen}</strong> Auszahlungszeilen über{" "}
                <strong>{euro(v.betrag)}</strong>
                {v.altbestandszeilen > 0 ? (
                  <span className="text-alert">
                    {" "}
                    — davon {v.altbestandszeilen} aus dem Altbestand
                  </span>
                ) : null}
              </li>
              {v.auszahlungsperioden.map((p) => (
                <li key={p.id} className="text-alert">
                  Auszahlungsperiode „{p.label}“ ({p.status})
                </li>
              ))}
              {v.importe.map((b) => (
                <li key={b.id}>
                  Import {sourceLabel(b.sourceType)}: {b.filename} ({b.zeilen} Zeilen)
                </li>
              ))}
            </ul>
          )}

          {v.importeTeilweise.length > 0 ? (
            <div className="mt-3 border-t border-line pt-2 text-xs text-muted">
              <div className="mb-1">Bleibt stehen, weil es über den Zeitraum hinausreicht:</div>
              <ul>
                {v.importeTeilweise.map((b) => (
                  <li key={b.id}>
                    {b.filename} ({b.from} bis {b.to})
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {betrifftAusgezahltes ? (
        <p className="mb-4 rounded border border-alert/40 bg-alert/5 px-4 py-3 text-sm">
          <strong className="text-alert">Achtung: hier wurde bereits ausgezahlt.</strong> Die
          Beträge sind überwiesen oder exportiert. Nach dem Zurücksetzen gibt es in der
          Software keinen Nachweis mehr darüber außer dem Audit-Eintrag.
        </p>
      ) : null}

      <label className="mb-4 block">
        <span className="label mb-1 block">Grund (Pflicht)</span>
        <input
          className="field"
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          placeholder="Warum wird zurückgesetzt? Steht später im Audit-Log."
        />
      </label>

      {loeschen.error ? <ErrorBox error={loeschen.error} /> : null}

      <div className="flex flex-wrap gap-2">
        <button
          className="btn-danger"
          disabled={grund.trim().length < 3 || loeschen.isPending || nichts}
          onClick={() => loeschen.mutate()}
        >
          {loeschen.isPending
            ? "Löscht"
            : v
              ? `${v.betriebstage} Tage ab ${longDate(bereich.from)} löschen`
              : "Löschen"}
        </button>
        <button className="btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}
